import { useCallback, useEffect, useRef, useState } from 'react';

interface UseMediaRecorderProps {
  onData: (data: Blob, mimeType: string) => void;
  maxSeconds?: number;
}

interface UseMediaRecorderReturn {
  startRecording: () => void;
  stopRecording: () => void;
  cancelRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  isRecording: boolean;
  isPaused: boolean;
  error: Error | null;
  audioLevel: number;
}

export default function useMediaRecorder({
  onData,
  maxSeconds,
}: UseMediaRecorderProps): UseMediaRecorderReturn {
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const skipUploadRef = useRef(false);

  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    const normalizedLevel = Math.min(average / 128, 1);
    setAudioLevel(normalizedLevel);

    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  }, []);

  const connectMicrophone = useCallback((stream: MediaStream) => {
    const audioContext = audioContextRef.current;
    const analyser = analyserRef.current;
    const destination = destinationRef.current;
    if (!audioContext || !analyser || !destination) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    source.connect(destination);
    sourceRef.current = source;
    streamRef.current = stream;
  }, []);

  const releaseMicrophone = useCallback(() => {
    sourceRef.current?.disconnect();
    sourceRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const resumeAudioContext = useCallback(() => {
    const audioContext = audioContextRef.current;
    if (!audioContext || audioContext.state === 'closed') {
      return Promise.resolve();
    }
    return audioContext.resume();
  }, []);

  const cleanupAudioAnalysis = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    destinationRef.current = null;
    setAudioLevel(0);
  }, []);

  const startRecording = () => {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const destination = audioContext.createMediaStreamDestination();

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    destinationRef.current = destination;

    Promise.all([
      navigator.mediaDevices.getUserMedia({ audio: true }),
      resumeAudioContext(),
    ])
      .then(([stream]) => {
        const chunks: BlobPart[] = [];

        connectMicrophone(stream);

        const recorder = new MediaRecorder(destination.stream);

        updateAudioLevel();

        recorder.ondataavailable = (e) => {
          chunks.push(e.data);
        };
        recorder.onstop = () => {
          releaseMicrophone();
          cleanupAudioAnalysis();
          if (!skipUploadRef.current) {
            const blob = new Blob(chunks, { type: 'audio/mp3' });
            onData(blob, 'audio/mp3');
          }
          skipUploadRef.current = false;
          setMediaRecorder(null);
          setIsPaused(false);
        };
        recorder.start();
        setError(null);
        setMediaRecorder(recorder);
        setIsPaused(false);
      })
      .catch((e) => {
        cleanupAudioAnalysis();
        setError(e);
      });
  };

  const stopRecording = () => {
    if (mediaRecorder?.state === 'paused') {
      mediaRecorder.resume();
    }
    mediaRecorder?.stop();
    setMediaRecorder(null);
  };

  const cancelRecording = () => {
    skipUploadRef.current = true;
    if (mediaRecorder?.state === 'paused') {
      mediaRecorder.resume();
    }
    mediaRecorder?.stop();
    setMediaRecorder(null);
  };

  const pauseRecording = () => {
    if (!mediaRecorder || mediaRecorder.state !== 'recording') return;
    mediaRecorder.pause();
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setAudioLevel(0);
    releaseMicrophone();
    setIsPaused(true);
  };

  const resumeRecording = () => {
    const recorder = mediaRecorder;
    if (!recorder || recorder.state !== 'paused' || !audioContextRef.current) {
      return;
    }

    Promise.all([
      navigator.mediaDevices.getUserMedia({ audio: true }),
      resumeAudioContext(),
    ])
      .then(([stream]) => {
        connectMicrophone(stream);
        if (recorder.state !== 'paused') return;
        recorder.resume();
        updateAudioLevel();
        setIsPaused(false);
      })
      .catch((e) => setError(e));
  };

  useEffect(() => {
    if (!mediaRecorder || !maxSeconds) return () => {};

    const timer = setTimeout(() => {
      stopRecording();
    }, maxSeconds * 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaRecorder, maxSeconds]);

  useEffect(
    () => () => {
      cleanupAudioAnalysis();
      releaseMicrophone();
    },
    [cleanupAudioAnalysis, releaseMicrophone]
  );

  return {
    startRecording,
    stopRecording,
    cancelRecording,
    pauseRecording,
    resumeRecording,
    isRecording: !!mediaRecorder,
    isPaused,
    error,
    audioLevel,
  };
}
